import { memo, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import {
  Bell,
  ChevronDown,
  FileText,
  LogOut,
  Menu,
  Package,
  Search,
  Settings,
  ShoppingBag,
  User,
  X,
  type LucideIcon,
} from 'lucide-react';
import { DynamicIcon, type IconName } from '@/components/Icon';
import { useTranslation } from 'react-i18next';
import { useDebounceValue } from 'usehooks-ts';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from '@/components/ui/navigation-menu';
import { useLogout } from '@/hooks/useAuth';
import { ROUTES } from '@/constants/routes';
import { GlobalSearchResults } from './GlobalSearchResults';
import { useAuthStore } from '@/stores/authStore';

type NavItem =
  | { label: string; to: string; Icon: LucideIcon; dynamicIcon?: never }
  | { label: string; to: string; dynamicIcon: IconName; Icon?: never };

const MORE_BTN_WIDTH = 96;
const NAV_GAP = 4;

function NavItemIcon({
  item,
  isActive,
  size = 16,
}: {
  item: NavItem;
  isActive: boolean;
  size?: number;
}) {
  return item.dynamicIcon ? (
    <DynamicIcon
      name={item.dynamicIcon}
      size={size}
      color={isActive ? 'var(--color-primary-s500)' : 'var(--color-lightGray)'}
    />
  ) : (
    <item.Icon
      size={size}
      className={isActive ? 'text-primary-s500' : 'text-lightGray'}
    />
  );
}

function DashboardHeader() {
  const { user, permissions } = useAuthStore();
  const logoutMutation = useLogout();

  // Defined inside the component so ROUTES is read at render time, not at
  // module-eval time — avoids a circular-import init crash under HMR.
  const NAV_ITEMS: NavItem[] = [
    { label: 'Order Management', to: ROUTES.ORDER_MANAGEMENT, Icon: ShoppingBag },
    { label: 'Warehouse', to: ROUTES.WAREHOUSE, Icon: Package },
    { label: 'Invoice', to: ROUTES.INVOICE, Icon: FileText },
    { label: 'Inventory Upload', to: ROUTES.INVENTORY_UPLOAD, dynamicIcon: 'inventory' },
    { label: 'User Management', to: ROUTES.USER_MANAGEMENT, Icon: User },
  ];

  const navItems = NAV_ITEMS.filter(item => {
    if (item.label === 'Inventory Upload') return permissions.canAccessInventory;
    if (item.label === 'User Management') return permissions.canAccessUserManagement;
    return true;
  });

  const location = useLocation();
  const { t } = useTranslation();

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebounceValue(searchQuery, 350);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(navItems.length);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const measureRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    if (!searchOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target as Node)
      ) {
        setSearchOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const calculate = () => {
      const available = nav.offsetWidth;
      const widths = measureRefs.current.map(el => el?.offsetWidth ?? 0);

      const totalWidth = widths.reduce(
        (sum, w, i) => sum + w + (i > 0 ? NAV_GAP : 0),
        0
      );
      if (totalWidth <= available) {
        setVisibleCount(navItems.length);
        return;
      }

      const budget = available - MORE_BTN_WIDTH - NAV_GAP;
      let used = 0;
      let count = 0;
      for (let i = 0; i < widths.length; i++) {
        const w = widths[i] + (i > 0 ? NAV_GAP : 0);
        if (used + w > budget) break;
        used += w;
        count++;
      }
      setVisibleCount(Math.max(1, count));
    };

    const ro = new ResizeObserver(calculate);
    ro.observe(nav);
    calculate();
    return () => ro.disconnect();
    // Only the item count affects layout; navItems is a fresh array each render.
  }, [navItems.length]);

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email?.split('@')[0] ||
    t('Guest');

  const visibleItems = navItems.slice(0, visibleCount);
  const overflowItems = navItems.slice(visibleCount);
  const isMoreActive = overflowItems.some(item =>
    location.pathname.startsWith(item.to)
  );

  return (
    <>
      {/* Hidden measurement nav — mirrors real nav item sizes */}
      <div aria-hidden className="pointer-events-none invisible fixed -top-full flex items-center gap-1">
        {navItems.map((item, i) => (
          <div
            key={item.label}
            ref={el => { measureRefs.current[i] = el; }}
            className="flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-base font-medium whitespace-nowrap"
          >
            <span className="h-4 w-4 shrink-0" />
            {item.label}
          </div>
        ))}
      </div>

      <header className="flex h-16 items-center justify-between border-b border-smoke bg-neutral-white px-4 lg:px-6">
        {/* Logo */}
        <Link
          to={ROUTES.ORDER_MANAGEMENT}
          search={{ page: 1, limit: undefined, search: undefined, status: undefined, tab: undefined, view: undefined }}
          className="flex shrink-0 items-center lg:mr-8"
        >
          <picture>
            <source
              media="(max-width: 767px)"
              srcSet="/gt-logo-119x28.webp 1x, /gt-logo-238x56.webp 2x"
            />
            <img
              src="/gt-logo-226x54.webp"
              srcSet="/gt-logo-226x54.webp 1x, /gt-logo-452x108.webp 2x"
              alt="GT Logo"
              width={120}
              height={29}
              className="h-7 w-auto"
            />
          </picture>
        </Link>

        {/* Nav Links — desktop only */}
        <NavigationMenu ref={navRef} className="hidden min-w-0 flex-1 lg:flex">
          <NavigationMenuList className="flex-nowrap gap-1">
            {visibleItems.map((item) => {
              const isActive = location.pathname === item.to;
              return (
                <NavigationMenuItem key={item.label}>
                  <Link
                    to={item.to}
                    className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-base font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'bg-primary-s50 text-primary-s500'
                        : 'text-lightGray hover:text-black'
                    }`}
                  >
                    <NavItemIcon item={item} isActive={isActive} />
                    {item.label}
                  </Link>
                </NavigationMenuItem>
              );
            })}

            {overflowItems.length > 0 && (
              <NavigationMenuItem>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={`flex shrink-0 items-center gap-1 rounded-full px-4 py-2 text-base font-medium whitespace-nowrap transition-colors ${
                        isMoreActive ? 'bg-primary-s50 text-primary-s500' : 'text-lightGray hover:text-black'
                      }`}
                    >
                      {t('More')}
                      <ChevronDown size={14} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-40 py-1">
                    {overflowItems.map((item) => {
                      const isActive = location.pathname === item.to;
                      return (
                        <Link
                          key={item.label}
                          to={item.to}
                          className={`flex items-center gap-2 px-3 py-2 text-base font-medium transition-colors ${
                            isActive
                              ? 'bg-primary-s50 text-primary-s500'
                              : 'text-lightGray hover:bg-smoke1 hover:text-black'
                          }`}
                        >
                          <NavItemIcon item={item} isActive={isActive} />
                          {item.label}
                        </Link>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </NavigationMenuItem>
            )}
          </NavigationMenuList>
        </NavigationMenu>

        {/* Right Actions */}
        <div className="flex items-center gap-1">
          {/* Animated Search — desktop only */}
          {/* The animated pill needs overflow-hidden to clip its own width
              transition, so the results panel has to live OUTSIDE it or it gets
              clipped away. This wrapper owns the positioning context instead. */}
          <div ref={searchContainerRef} className="relative hidden lg:block">
          <div
            className={`flex items-center overflow-hidden rounded-full border transition-[width,border-color,box-shadow,background-color] duration-300 ease-in-out ${
              searchOpen
                ? 'w-72 border-smoke bg-neutral-white focus-within:border-primary-s500 focus-within:ring-2 focus-within:ring-primary-s500/20'
                : 'w-9 border-transparent bg-transparent'
            }`}
          >
            <button
              type="button"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full hover:bg-smoke1"
              onClick={() => setSearchOpen(true)}
            >
              <Search className="h-5 w-5" />
            </button>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('Search orders, parts, invoices...')}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSearchQuery('');
                  setSearchOpen(false);
                }
              }}
              /* @tailwindcss/forms gives every input a 1px border and a square
                 focus ring. Both are stripped here so the rounded pill owns the
                 focus affordance via focus-within. */
              className={`h-9 min-w-0 border-0 bg-transparent pr-3 text-sm text-black shadow-none transition-opacity duration-300 outline-none placeholder:text-lightGray focus:border-0 focus:shadow-none focus:ring-0 ${
                searchOpen ? 'w-full opacity-100' : 'w-0 opacity-0'
              }`}
            />
          </div>
            {searchOpen && (
              <GlobalSearchResults
                query={debouncedSearch}
                onClose={() => {
                  setSearchQuery('');
                  setSearchOpen(false);
                }}
              />
            )}
          </div>

          <Button asChild variant="ghost" size="icon" className="hidden h-9 w-9 rounded-full lg:flex">
            <Link to={ROUTES.SETTINGS} aria-label={t('Settings')}>
              <Settings size={18} className="text-lightGray" />
            </Link>
          </Button>

          <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full" type="button">
            <Bell size={18} className="text-lightGray" />
          </Button>

          {/* User dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="ml-1 flex cursor-pointer items-center gap-2 rounded-full px-2 py-1 hover:bg-smoke1"
              >
                <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-smoke">
                  <User size={18} className="text-lightGray" />
                </div>
                <span className="hidden text-sm font-semibold text-black lg:inline">{displayName}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem onClick={() => logoutMutation.mutate()}>
                <LogOut size={16} className="text-lightGray" />
                {t('Logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Hamburger — mobile only */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-smoke1 lg:hidden"
          >
            {mobileMenuOpen ? <X size={20} className="text-lightGray" /> : <Menu size={20} className="text-lightGray" />}
          </button>
        </div>
      </header>

      {/* Mobile Nav Drawer */}
      {mobileMenuOpen && (
        <div className="border-b border-smoke bg-neutral-white lg:hidden">
          <nav className="flex flex-col px-4 py-3">
            {navItems.map((item) => {
              const isActive = location.pathname === item.to;
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 text-base font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-s50 text-primary-s500'
                      : 'text-lightGray hover:bg-smoke1 hover:text-black'
                  }`}
                >
                  <NavItemIcon item={item} isActive={isActive} size={18} />
                  {item.label}
                </Link>
              );
            })}

            <div className="mt-2 border-t border-smoke pt-3">
              <button
                type="button"
                onClick={() => logoutMutation.mutate()}
                className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-base font-medium text-danger hover:bg-danger-light"
              >
                <LogOut size={18} />
                {t('Logout')}
              </button>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}

export default memo(DashboardHeader);
